import {
  default as ButtonPrimary,
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import FormInputBase from "@rentiohq/shared-frontend/dist/components/components/FormInputBase";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  SegmentedControl,
  RichTextInput,
  TextField,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import * as systemActions from "@rentiohq/web-shared/dist/redux/system/system.actions";
import * as systemSelectors from "@rentiohq/web-shared/dist/redux/system/system.selectors";
import { EPreferencePersistScope } from "@rentiohq/web-shared/dist/redux/system/system.types";
import {
  getMarkerFinding,
  getMarkerFindingMessage,
  IMarkerFinding,
  IMarkerRule,
} from "forms/propertyApplicationNotEligible/schema.propertyApplicationNotEligible.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { getPreferencesToSave } from "./TenantProfileNotificationPreferenceGroup.utils";
import { useIsNotificationEnabled } from "./useIsNotificationEnabled";

interface IProps {
  /**
   * The link a text of this kind must keep, when it has one. This screen is
   * the second place these texts are written, and until DEV-3323 it enforced
   * nothing: the rule lived in the step-change form's schema, and this is not
   * a schema form but a hand-rolled panel with its own save button. A broker
   * could therefore break the link here and have the modal refuse the
   * identical text later. The rule itself is imported rather than restated,
   * so the two screens cannot drift apart.
   */
  markerRules?: IMarkerRule[];
  /**
   * Absent for a mail the broker may rewrite but never switch off, which is
   * the whole point of leaving it out: no key means no switch, and there is
   * nothing stored that a later change could flip (DEV-3317). The texts are
   * then always shown, because there is no off state to hide them behind.
   */
  enabledKey?: string;
  /**
   * Key to follow while `enabledKey` has never been written. Lets a block that
   * splits off from an existing one inherit the broker's current choice instead
   * of silently switching itself on (DEV-3206): whoever had confirmations off
   * stays off on both mails until they say otherwise.
   */
  fallbackEnabledKey?: string;
  /**
   * False for a block that shares another block's switch. The two rejection
   * texts are one mail with two wordings, so one switch governs both, and a
   * second control bound to the same key would only invite them to drift
   * apart on screen (DEV-3325).
   */
  showSwitch?: boolean;
  /**
   * A line under the switch saying when this mail goes out. For a block with
   * no editable text there is nothing else on the screen to explain it, and
   * an office deciding whether to switch it off should not have to guess at
   * the rule (DEV-3329).
   */
  switchInfo?: string;
  /**
   * The office's own name for this block, stored as one value rather than one
   * per language: only the office reads it, and it is never sent. Absent for
   * a block that needs no name.
   */
  nameKey?: string;
  nameLabel?: string;
  /** Shown in the name field, and used in the move modal, while it is empty. */
  defaultName?: string;
  enabledByDefault: boolean;
  preferencesConfig: {
    label: string;
    key: string;
    defaultValue: string;
    /**
     * Where a box reads from while nothing is stored for it. The second
     * rejection text points at the first, so an office that wrote its own
     * rejection before this release finds that text in both places instead
     * of the generic default in one of them (DEV-3325).
     */
    fallbackKey?: string;
  }[];
  /**
   * Lines under the boxes, above the save button.
   *
   * For what the sender does to the text after the office is done with it:
   * what it appends, and which variables it fills in. Those belong next to
   * the boxes rather than next to the switch, because they are about what
   * the broker just typed.
   */
  fieldsInfo?: string[];
}

const TenantProfileNotificationPreferenceGroup = (props: IProps) => {
  const {
    markerRules,
    enabledKey,
    fallbackEnabledKey,
    showSwitch = true,
    switchInfo,
    nameKey,
    nameLabel,
    defaultName,
    preferencesConfig,
    fieldsInfo,
    enabledByDefault = true,
  } = props;

  // Redux
  const dispatch = useDispatch();

  const isEnabled = useIsNotificationEnabled({
    enabledKey,
    fallbackEnabledKey,
    enabledByDefault,
  });

  // What a box falls back to while nothing is stored for it: another block's
  // text where one is named, its own default otherwise.
  const fallbackPreferences = useSelector((state: IRootStore) =>
    systemSelectors.getPreferences(
      state,
      preferencesConfig.map(preferenceConfig => ({
        key: preferenceConfig.fallbackKey || preferenceConfig.key,
        defaultValue: preferenceConfig.defaultValue,
      })),
    ),
  );

  const preferences = useSelector((state: IRootStore) =>
    systemSelectors.getPreferences(
      state,
      preferencesConfig.map((preferenceConfig, index) => ({
        key: preferenceConfig.key,
        defaultValue: `${
          fallbackPreferences[index]?.value ?? preferenceConfig.defaultValue
        }`,
      })),
    ),
  );

  const storedName = useSelector((state: IRootStore) =>
    nameKey ? systemSelectors.getPreference<string>(state, nameKey) : undefined,
  );

  // State
  const [cachedPreferences, setCachedPreferences] = React.useState<
    typeof preferences
  >([]);

  // Which language boxes were refused on the last save attempt. Per box
  // rather than one message for the group: these mails have a text per
  // language, and a broker told only that "the link is missing" has three
  // boxes to go hunting through.
  const [problemByKey, setProblemByKey] = React.useState<{
    [key: string]: IMarkerFinding;
  }>({});

  const [cachedName, setCachedName] = React.useState("");

  // Lifecycle
  React.useEffect(() => {
    setCachedPreferences(preferences);
    setCachedName(`${storedName || ""}`);
    setProblemByKey({});
  }, [preferencesConfig]);

  // Event handlers
  const handleSwitchEnabled = (newValue: boolean) => {
    if (!enabledKey) {
      return;
    }

    dispatch(
      systemActions.setPreferences({
        persistScope: EPreferencePersistScope.RemoteBroker,
        preferences: {
          [enabledKey]: !newValue,
        },
      }),
    );

    showAlert({
      type: "success",
      message: getLocalizedText("preference.updated.alert.title"),
    });
  };

  const handlePressSave = () => {
    if (markerRules && markerRules.length > 0) {
      const problems: { [key: string]: IMarkerFinding } = {};

      cachedPreferences.forEach(cachedPreference => {
        const finding = getMarkerFinding(
          `${cachedPreference.value || ""}`,
          markerRules,
        );

        if (finding) {
          problems[cachedPreference.key] = finding;
        }
      });

      setProblemByKey(problems);

      const firstProblem = Object.values(problems)[0];

      if (firstProblem) {
        // The boxes carry the detail; this says the save did not happen, for
        // a broker whose failing language has scrolled out of view.
        showAlert({
          type: "error",
          message: getMarkerFindingMessage(firstProblem),
        });
        return;
      }
    }

    // What clearing a box would show: the text a box with a `fallbackKey`
    // inherits, its own default otherwise. A second rejection text reset to
    // the generic default while the first holds the office's own text is
    // therefore still written, since clearing it would show the office's
    // text instead of the one they picked.
    const textWhenClearedByKey: { [key: string]: string } = {};

    preferencesConfig.forEach((preferenceConfig, index) => {
      textWhenClearedByKey[preferenceConfig.key] = preferenceConfig.fallbackKey
        ? `${
            fallbackPreferences[index]?.value ?? preferenceConfig.defaultValue
          }`
        : preferenceConfig.defaultValue;
    });

    const preferencesToSet: { [key: string]: string | null } =
      getPreferencesToSave({
        values: cachedPreferences,
        textWhenClearedByKey,
      });

    // Saved by the same button as the texts it names, so a broker who renamed
    // a block and rewrote it does not have to discover that those were two
    // separate saves.
    if (nameKey) {
      preferencesToSet[nameKey] = cachedName;
    }

    dispatch(
      systemActions.setPreferences({
        persistScope: EPreferencePersistScope.RemoteBroker,
        preferences: preferencesToSet,
      }),
    );

    showAlert({
      type: "success",
      message: getLocalizedText("preference.updated.alert.title"),
    });
  };

  // Render
  return (
    <>
      {enabledKey && showSwitch && (
        <FormInputBase
          title={getLocalizedText("tenant_profile.notification.enabled.title")}
          onPressExtraInfo={() => {}}
          showBorder={false}
        >
          <div style={{ maxWidth: "600px" }}>
            <Spacer weight={ESpacerWeight.W08} />

            <SegmentedControl
              name={""}
              options={[
                {
                  id: 1,
                  value: getLocalizedText("system.enabled"),
                },
                {
                  id: 0,
                  value: getLocalizedText("system.disabled"),
                },
              ]}
              selected={isEnabled ? 1 : 0}
              onChange={(value: number) => {
                handleSwitchEnabled(!value);
              }}
            />

            {switchInfo && (
              <>
                <Spacer weight={ESpacerWeight.W08} />
                <TextStyle variation="subdued" size="small">
                  {switchInfo}
                </TextStyle>
              </>
            )}
          </div>
        </FormInputBase>
      )}

      {/* A block without a switch has nowhere else to say what it is: the
          line above lives inside the switch, and this one is the same line
          for a block that has none (DEV-3335). */}
      {switchInfo && !(enabledKey && showSwitch) && (
        <div style={{ maxWidth: "600px" }}>
          <Spacer weight={ESpacerWeight.W08} />
          <TextStyle variation="subdued" size="small">
            {switchInfo}
          </TextStyle>
        </div>
      )}

      {isEnabled && (
        <>
          <Spacer weight={ESpacerWeight.W16} />

          {nameKey && (
            <div style={{ maxWidth: "600px" }}>
              <TextField
                id={nameKey}
                name={nameKey}
                label={nameLabel}
                value={cachedName}
                placeholder={defaultName}
                onChange={event => setCachedName(event.target.value)}
              />
              <Spacer weight={ESpacerWeight.W16} />
            </div>
          )}

          {cachedPreferences.map((preference, index) => {
            const preferenceConfig = props.preferencesConfig.find(
              randomPreferenceConfig =>
                randomPreferenceConfig.key === preference.key,
            );
            if (!preferenceConfig) {
              return null;
            }

            return (
              <React.Fragment key={preferenceConfig.key}>
                {index > 0 && <Spacer weight={ESpacerWeight.W32} />}

                {/* The same editor as the bulk-action mail and the
                    step-change modal, so a broker meets one toolbar rather
                    than three (DEV-3323). It reads its own HTML back, which
                    a textarea could only show as literal markup. */}
                <RichTextInput
                  label={preferenceConfig.label}
                  value={`${preference.value || ""}`}
                  error={
                    problemByKey[preference.key]
                      ? getMarkerFindingMessage(problemByKey[preference.key])
                      : undefined
                  }
                  onChange={(value: string) => {
                    // The red goes as soon as they start fixing it, rather
                    // than staying until the next save attempt.
                    if (problemByKey[preference.key]) {
                      setProblemByKey(previous => {
                        const next = { ...previous };
                        delete next[preference.key];
                        return next;
                      });
                    }

                    setCachedPreferences(
                      cachedPreferences.map(cachedPreference => {
                        if (cachedPreference.key !== preference.key) {
                          return cachedPreference;
                        }

                        return {
                          ...cachedPreference,
                          value,
                        };
                      }),
                    );
                  }}
                />

                <div style={{ marginTop: "4px" }}>
                  {/* Always clickable. It used to disable itself when the
                      value equalled the default, which compares a saved text
                      against a plain-text default and so was never true once
                      the broker had saved, and always true before they had:
                      the button read as dead in both cases (Jonas,
                      2026-10-03). Restoring a text that is already the
                      default costs nothing. */}
                  <Button
                    appearance="ghost"
                    size="slim"
                    onClick={() => {
                      // The default always satisfies the rule, so whatever
                      // was flagged here no longer applies.
                      if (problemByKey[preference.key]) {
                        setProblemByKey(previous => {
                          const next = { ...previous };
                          delete next[preference.key];
                          return next;
                        });
                      }

                      setCachedPreferences(
                        cachedPreferences.map(cachedPreference => {
                          if (cachedPreference.key !== preference.key) {
                            return cachedPreference;
                          }

                          return {
                            ...cachedPreference,
                            value: preferenceConfig.defaultValue,
                          };
                        }),
                      );
                    }}
                  >
                    {getLocalizedText("system.reset")}
                  </Button>
                </div>
              </React.Fragment>
            );
          })}

          {!!preferencesConfig.length && !!fieldsInfo?.length && (
            <>
              <Spacer weight={ESpacerWeight.W16} />

              {fieldsInfo.map(line => (
                <div key={line}>
                  <TextStyle variation="subdued" size="small">
                    {line}
                  </TextStyle>
                </div>
              ))}
            </>
          )}

          {/* A block that is only a switch has nothing to save: the switch
              writes its own preference the moment it is flipped, so a save
              button there would do nothing and suggest the switch had not
              taken yet (DEV-3329). */}
          {!!preferencesConfig.length && (
            <>
              <Spacer weight={ESpacerWeight.W24} />

              <ButtonPrimary
                title={getLocalizedText("system.save")}
                onPress={handlePressSave}
                buttonSize={EButtonSize.Medium}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default TenantProfileNotificationPreferenceGroup;
